import { ConversationMessageReaction as ConversationMessageReactionEntity } from '@provision/entity-types';
import { useApolloClient } from '@provision/graphql-client';
import { useEffect } from 'react';
import { filter } from 'rxjs/operators';
import { wsEventBus } from 'src/core/realtime-connection';
import {
  removeReactionFromCache,
  upsertReactionInCache,
} from 'src/graphql/entities/conversation-message-reaction/conversation-message-reaction.cache';
import { REACTION_PLATFORM_USER_FRAGMENT } from 'src/graphql/entities/conversation-message-reaction/conversation-message-reaction.operations';
import { ReactionPlatformUser } from 'src/graphql/entities/conversation-message-reaction/conversation-message-reaction.types';

/**
 * Applies reactions added or removed by anyone to the cached messages of one conversation.
 * Updates are keyed by user and emoji, so the echo of the viewer's own change is a no-op.
 */
export const useMessageReactionsSubscription = (conversationId?: string) => {
  const { cache } = useApolloClient();

  useEffect(() => {
    if (!conversationId) {
      return;
    }

    const createSubscription = wsEventBus
      .onEntity<ConversationMessageReactionEntity>({
        name: 'ConversationMessageReaction',
        operation: 'create',
      })
      .pipe(filter((event) => event.attributes.conversationId === conversationId))
      .subscribe(({ attributes }) => {
        // Event payloads carry no relations; the reactor's name comes from any cached copy.
        const platformUser = cache.readFragment<ReactionPlatformUser>({
          id: cache.identify({ __typename: 'PlatformUser', id: attributes.userId }),
          fragment: REACTION_PLATFORM_USER_FRAGMENT,
        });
        upsertReactionInCache(cache, {
          id: attributes.id,
          conversationMessageReactionId: attributes.conversationMessageReactionId,
          conversationMessageId: attributes.conversationMessageId,
          conversationId: attributes.conversationId,
          partnerId: attributes.partnerId,
          userId: attributes.userId,
          emoji: attributes.emoji,
          createdAt: attributes.createdAt,
          platformUser: platformUser ? { ...platformUser, __typename: 'PlatformUser' } : null,
        });
      });

    const deleteSubscription = wsEventBus
      .onEntity<ConversationMessageReactionEntity>({
        name: 'ConversationMessageReaction',
        operation: 'delete',
      })
      .pipe(filter((event) => event.previousAttributes.conversationId === conversationId))
      .subscribe(({ previousAttributes }) =>
        removeReactionFromCache(cache, previousAttributes),
      );

    return () => {
      createSubscription.unsubscribe();
      deleteSubscription.unsubscribe();
    };
  }, [cache, conversationId]);
};
